import React from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import Autocomplete from "@mui/material/Autocomplete";
import { FormControl } from "@mui/material";

import { StyledTextFieldBootstrap } from "../../doctor-console/StyledComponents";
import { BED_STATUS } from "../../ipd/utils/bedStatus";
import { createBed } from "../api/bedMasterApi";

/**
 * Add New Bed — the form half of the Bed Management screen, laid out like
 * FacilityUsers / DoctorsAddition (bootstrap form-control + react-hook-form).
 *
 * Bed Type is a picker rather than free text because the create payload keys off
 * bed_type_code. The option list comes from BedManagement, which loads it once for
 * this form and the list's edit modal.
 *
 * There is no Department field: a bed belongs to a ward (bed type) and to no
 * department, so one physical bed is entered exactly once and any department can
 * admit into it.
 */
function BedAddition({ updateList, bedTypes }) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { status: "available", is_active: true } });

  const onSubmit = async (data, e) => {
    e.preventDefault();

    const post_data = {
      name: data.name.trim(),
      code: data.code.trim(),
      bed_type_code: data.bed_type_code,
      location: data.location?.trim() || "",
      status: data.status,
      is_active: !!data.is_active,
    };

    try {
      const res = await createBed(post_data);
      if (res?.status === "Success") {
        toast.success(res?.message || "Bed created successfully");
        reset({ status: "available", is_active: true });
        updateList();
      } else {
        toast.error(res?.message || "Could not create bed");
      }
    } catch (err) {
      console.error("createBed:", err);
      toast.error(err?.response?.data?.message || "Could not create bed");
    }
  };

  return (
    <div>
      <div className="col-sm-12 col-md-12 col-lg-10" style={{ margin: "auto" }}>
        <div className="contact-panel mb-50" style={{ marginTop: "50px" }}>
          <h4 style={{ textAlign: "center" }}>Add New Bed</h4>
          <form
            className="contact-panel__form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="name" className="required-label">
                  Bed Name{" "}
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Bed Name"
                    name="name"
                    {...register("name", { required: "Bed Name is required." })}
                  />
                  {errors.name && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.name.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="code" className="required-label">
                  Bed Code{" "}
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Bed Code (e.g. DLX-01)"
                    name="code"
                    {...register("code", { required: "Bed Code is required." })}
                  />
                  {errors.code && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.code.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="bed_type_code" className="required-label">
                  Bed Type{" "}
                </label>
                <div className="form-group">
                  <FormControl fullWidth error={!!errors.bed_type_code}>
                    <Controller
                      name="bed_type_code"
                      control={control}
                      rules={{ required: "Bed Type is required." }}
                      render={({ field }) => (
                        <Autocomplete
                          options={bedTypes}
                          getOptionLabel={(option) =>
                            option?.name
                              ? `${option.name} (${option.code})`
                              : option?.code || ""
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.code === value?.code
                          }
                          renderInput={(params) => (
                            <StyledTextFieldBootstrap
                              {...params}
                              placeholder="Select Bed Type"
                            />
                          )}
                          value={
                            bedTypes.find((bt) => bt.code === field.value) ||
                            null
                          }
                          onChange={(_, value) =>
                            field.onChange(value ? value.code : "")
                          }
                        />
                      )}
                    />
                    {errors.bed_type_code && (
                      <p className="errorMsg" style={{ color: "red" }}>
                        {errors.bed_type_code.message}
                      </p>
                    )}
                  </FormControl>
                </div>
              </div>

              {/* No Room field: the bed's room (and the ward / room category
                  derived from it) is not settable through ipd_bed/ — create
                  and update both ignore a room_code, so an input here would
                  silently drop what the user typed. The list still shows the
                  room, which GET does return. */}
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="location">Location </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Location (e.g. 3rd floor)"
                    name="location"
                    {...register("location")}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="status">Status </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    name="status"
                    {...register("status")}
                  >
                    {Object.entries(BED_STATUS).map(([value, cfg]) => (
                      <option key={value} value={value}>
                        {cfg.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                className="col-sm-3 col-md-3 col-lg-3"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "3%",
                }}
              >
                <div className="form-group">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="is_active"
                      {...register("is_active")}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-12 d-flex" style={{ padding: "0px" }}>
                <div className="col-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn__secondary btn__rounded btn__block mt-2"
                    style={{
                      backgroundColor: "#0E3F66",
                      color: "#FFFFFF",
                      borderRadius: "10px",
                      padding: "24px 140px",
                      gap: "10px",
                    }}
                  >
                    <span>Add Bed</span> <i className="icon-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BedAddition;
